import { Auth } from 'auth-provider';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import useInterval from 'use-interval';

/**
 * The supported states of authentication that can be rendered in the app.
 */
export type AuthState = 'unknown' | 'signedin' | 'loading' | 'signedout';

const postLoginUrlKey = 'post.login.url';
const amplifyRedirectedKey = 'amplify-redirected-from-hosted-ui';

/**
 * Store the current URL as the post login URL in session storage.
 */
export function setPostLoginUrl() {
    sessionStorage.setItem(postLoginUrlKey, window.location.href);
}

/**
 * Clear the post login URL from session storage.
 */
export function clearPostLoginUrl() {
    sessionStorage.removeItem(postLoginUrlKey);
}

/**
 */

/**
 * If redirect is true and if a post login URL is stored in session storage, the URL will
 * be cleared from session storage and the application will be redirected to that URL.
 *
 * @param redirect If this value changes to true, the redirect will be attempted.
 */
export function usePostLoginRedirect(redirect = true) {
    useEffect(() => {
        if (redirect) {
            const value = sessionStorage.getItem(postLoginUrlKey);
            if (value) {
                clearPostLoginUrl();
                try {
                    const url = new URL(value);
                    // check to make sure the redirect actually points to the current app
                    if (url.origin === window.location.origin) {
                        window.location.href = url.href;
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
        }
    }, [redirect]);
}

/**
 * Periodically waits for the user to become authenticated and runs a callback when the user is authenticated.
 * @param onSignIn The callback to call when authenticated.
 * @param interval (optional) How often to check (in milliseconds) if the user is authenticated.
 */
export function useWaitForAuthentication(
    onSignIn: () => void,
    interval: number | null = 100
) {
    async function checkSession() {
        try {
            const session = await Auth.currentSession();
            if (session.isValid()) {
                onSignIn();
            }
        } catch {
            // Session doesn't exist, so we don't care.
        }
    }

    useInterval(() => checkSession(), interval);
}

/**
 * Returns the current authentication state of the app, and a function to update it.
 */
export function useAuthState(): [AuthState, Dispatch<SetStateAction<AuthState>>] {
    const [authState, setAuthState] = useState<AuthState>('unknown');

    useEffect(() => {
        async function checkIfSignedIn() {
            // Might be authenticated, so see if we can get a valid session.
            try {
                const session = await Auth.currentSession();
                if (session.isValid()) {
                    setAuthState('signedin');
                } else {
                    setAuthState('signedout');
                }
            } catch {
                // Couldn't get a current session, so definitely signed out.
                setAuthState('signedout');
            }
        }
        const redirected = localStorage.getItem(amplifyRedirectedKey);

        if (redirected === 'true') {
            localStorage.removeItem(amplifyRedirectedKey);
            setAuthState('loading');
        } else {
            checkIfSignedIn();
        }
    }, []);

    return [authState, setAuthState];
}
